import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import MemberAvatar from '@/components/MemberAvatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import {
  LogOut, Home, Users, Calendar, BookOpen, MessageCircle, FileText, Newspaper,
  Award, Settings, Bell, Crown, FlaskConical, ChevronDown, Compass, User, Menu, X, Shield, MessageSquarePlus, Building2, MessageSquare, Headphones, Briefcase, ScanLine
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

function NotificationBell({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const token = sessionStorage.getItem('session_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = () => {
    fetch(`${BACKEND_URL}/api/user-notifs`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(data => {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      })
      .catch(() => {});
  };

  const markAllRead = () => {
    fetch(`${BACKEND_URL}/api/user-notifs/read`, {
      method: 'POST', credentials: 'include', headers
    }).then(() => {
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    });
  };

  const handleClick = (notif) => {
    if (!notif.read) {
      fetch(`${BACKEND_URL}/api/user-notifs/${notif.notification_id}/read`, {
        method: 'POST', credentials: 'include', headers
      });
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n));
    }
    setOpen(false);
    if (notif.link) {
      if (notif.type === 'news' && notif.link.startsWith('http')) {
        window.open(notif.link, '_blank');
      } else {
        window.location.href = notif.link;
      }
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell"
        onClick={() => {
          const next = !open;
          setOpen(next);
          // Opening the panel implicitly marks everything as seen — this matches
          // the user's mental model ("I just looked at them").
          if (next && unreadCount > 0) {
            fetch(`${BACKEND_URL}/api/user-notifs/read`, { method: 'POST', credentials: 'include', headers })
              .then(() => {
                setUnreadCount(0);
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
              })
              .catch(() => {});
          }
        }}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors">
        <Bell className="h-5 w-5 text-slate-600" />
        {unreadCount > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-[100] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button data-testid="mark-all-read" onClick={markAllRead}
                className="text-xs text-brand-navy hover:text-brand-dark font-medium">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length > 0 ? notifications.slice(0, 15).map(notif => (
              <button key={notif.notification_id} data-testid={`notif-${notif.notification_id}`}
                onClick={() => handleClick(notif)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 ${
                  !notif.read ? 'bg-brand-navy/5' : ''
                }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  notif.type === 'message' ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'
                }`}>
                  {notif.type === 'message' ? <MessageCircle className="h-4 w-4" /> : <Newspaper className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-tight ${!notif.read ? 'font-semibold' : 'font-medium'} truncate`}>
                    {notif.title}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{notif.body}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{timeAgo(notif.created_at)} ago</p>
                </div>
                {!notif.read && <div className="w-2 h-2 bg-brand-navy/50 rounded-full flex-shrink-0 mt-2" />}
              </button>
            )) : (
              <div className="p-8 text-center text-sm text-slate-400">No notifications yet</div>
            )}
          </div>
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100 text-center">
              <Link to="/notifications" onClick={() => setOpen(false)}
                className="text-xs text-brand-navy hover:text-brand-dark font-medium"
                data-testid="view-all-notifications">
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Navigation({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => {
      fetch(`${BACKEND_URL}/api/messages/stats`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setUnreadMessages(data.unread || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = async () => {
    try {
      const storedToken = sessionStorage.getItem('session_token');
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionStorage.removeItem('session_token');
      sessionStorage.removeItem('sll_user');
      localStorage.removeItem('sll_session_token');
      localStorage.removeItem('sll_user');
      toast.success('Logged out successfully');
      navigate('/login');
    }
  };

  const isActive = (path) => location.pathname === path;
  const isAdmin = user?.role === 'admin';

  const enablerItems = [
    { path: '/events', icon: Calendar, label: 'Events' },
    { path: '/courses', icon: BookOpen, label: 'Courses' },
    { path: '/keynotes', icon: FileText, label: 'Keynotes' },
    { path: '/britecast', icon: Headphones, label: 'BriteCast' },
    { path: '/learning', icon: FileText, label: 'Learning' },
    { path: '/news', icon: Newspaper, label: 'News' },
    { path: '/companies', icon: Building2, label: 'Companies' },
    { path: '/jobs', icon: Briefcase, label: 'Jobs' },
  ];

  const enablerActive = enablerItems.some(i => isActive(i.path));

  const NavLink = ({ to, icon: Icon, label, testId }) => (
    <Link to={to}>
      <Button data-testid={testId || `nav-${label.toLowerCase()}-link`} variant="ghost" size="sm"
        className={`flex items-center gap-1.5 text-sm ${
          isActive(to) ? 'bg-brand-navy/5 text-brand-navy font-medium' : 'text-slate-600 hover:text-slate-900'
        }`}>
        <Icon className="h-4 w-4" />
        <span>{label}</span>
      </Button>
    </Link>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/progress" className="flex items-center space-x-2 flex-shrink-0">
            <img src="/assets/britehub-logo.png" alt="SLL BriteHub" className="h-8 w-8" />
            <span className="text-2xl font-bold font-heading gradient-text">SLL BriteHub</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            <NavLink to="/progress" icon={Home} label="Progress" />

            {/* Enablers Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="nav-enablers-dropdown" variant="ghost" size="sm"
                  className={`flex items-center gap-1.5 text-sm ${
                    enablerActive ? 'bg-brand-navy/5 text-brand-navy font-medium' : 'text-slate-600 hover:text-slate-900'
                  }`}>
                  <Compass className="h-4 w-4" />
                  <span>Enablers</span>
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                {enablerItems.map(item => (
                  <DropdownMenuItem key={item.path} data-testid={`nav-${item.label.toLowerCase()}-link`}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-2 cursor-pointer ${isActive(item.path) ? 'bg-brand-navy/5 text-brand-navy' : ''}`}>
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <NavLink to="/forum" icon={MessageSquare} label="Forum" />

            <NavLink to="/britelabs" icon={FlaskConical} label="BriteLab" />
            <NavLink to="/members" icon={Users} label="Members" />
            <NavLink to="/partners" icon={Building2} label="Partners" />
            <Link to="/messages">
              <Button data-testid="nav-messages-link" variant="ghost" size="sm"
                className={`flex items-center gap-1.5 text-sm relative ${
                  isActive('/messages') ? 'bg-brand-navy/5 text-brand-navy font-medium' : 'text-slate-600 hover:text-slate-900'
                }`}>
                <MessageCircle className="h-4 w-4" />
                <span>Messages</span>
                {unreadMessages > 0 && (
                  <span data-testid="nav-messages-unread-badge" className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold px-1 ml-0.5">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </Button>
            </Link>
          </div>

          {/* Right side: bell + profile dropdown */}
          <div className="flex items-center gap-2">
            <NotificationBell user={user} />

            {/* Profile / Account Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-profile-link" className="flex items-center gap-1.5 p-1 rounded-lg hover:bg-slate-100 transition-colors">
                  <MemberAvatar name={user?.name} picture={user?.picture} size={32} />
                  <ChevronDown className="h-3 w-3 text-slate-400 hidden lg:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="font-normal">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-profile-menu-item" onClick={() => navigate('/profile')}
                  className="flex items-center gap-2 cursor-pointer">
                  <User className="h-4 w-4" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-security-link" onClick={() => navigate('/profile?section=security')}
                  className="flex items-center gap-2 cursor-pointer">
                  <Shield className="h-4 w-4" /> Security
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-feedback-link" onClick={() => navigate('/feedback')}
                  className="flex items-center gap-2 cursor-pointer">
                  <MessageSquarePlus className="h-4 w-4" /> Feedback
                </DropdownMenuItem>
                {/* Pricing link intentionally removed from the profile menu —
                    members go through Admin / direct sales rather than a
                    self-serve flow. The /pricing route still exists. */}
                {isAdmin && (
                  <DropdownMenuItem data-testid="nav-admin-link" onClick={() => navigate('/admin')}
                    className="flex items-center gap-2 cursor-pointer">
                    <Settings className="h-4 w-4" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={handleLogout}
                  className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut className="h-4 w-4" /> Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile hamburger */}
            <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-5 w-5 text-slate-600" /> : <Menu className="h-5 w-5 text-slate-600" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Slide-out */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-100 py-2 pb-3 space-y-0.5 max-h-[70vh] overflow-y-auto" data-testid="mobile-nav-menu">
            {[
              { path: '/progress', icon: Home, label: 'Progress' },
              ...enablerItems,
              { path: '/forum', icon: MessageSquare, label: 'Forum' },
              { path: '/britelabs', icon: FlaskConical, label: 'BriteLab' },
              { path: '/members', icon: Users, label: 'Members' },
              { path: '/partners', icon: Building2, label: 'Partners' },
              { path: '/messages', icon: MessageCircle, label: 'Messages' },
              ...(isAdmin ? [{ path: '/admin', icon: Settings, label: 'Admin Panel' }] : []),
              { path: '/profile', icon: User, label: 'Profile' },
            ].map(item => (
              <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive(item.path) ? 'bg-brand-navy/5 text-brand-navy font-medium' : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <item.icon className="h-4 w-4" />
                {item.label}
                {item.path === '/messages' && unreadMessages > 0 && (
                  <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold px-1">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </Link>
            ))}
            <button onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 w-full transition-colors">
              <LogOut className="h-4 w-4" /> Log Out
            </button>
          </div>
        )}
      </div>

      {/* Mobile Bottom Tab Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]" data-testid="mobile-bottom-tabs">
        <div className="flex items-center justify-around px-1 py-1.5">
          {[
            { path: '/progress', icon: Home, label: 'Home' },
            { path: '/forum', icon: MessageSquare, label: 'Forum' },
            { path: '/britelabs', icon: FlaskConical, label: 'BriteLab' },
            // Admin-only: replace the Members tab with the QR Scan entry
            // since admins already reach Members via /admin. Keeps the
            // most-used tool one tap away during an event without
            // crowding the bar with a 6th tile.
            user?.role === 'admin'
              ? { path: '/admin/check-in', icon: ScanLine, label: 'Scan' }
              : { path: '/members', icon: Users, label: 'Members' },
            { path: '/messages', icon: MessageCircle, label: 'Messages', badge: unreadMessages },
          ].map(tab => (
            <Link key={tab.path} to={tab.path}
              data-testid={`mobile-tab-${tab.label.toLowerCase()}`}
              className={`flex flex-col items-center justify-center px-2 py-1 rounded-lg relative min-w-[3.5rem] transition-colors ${
                isActive(tab.path)
                  ? 'text-brand-navy'
                  : 'text-slate-400 active:text-slate-600'
              }`}>
              <tab.icon className="h-5 w-5" />
              <span className="text-[10px] mt-0.5 font-medium leading-tight">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute -top-0.5 right-0.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-rose-500 text-white text-[9px] font-bold px-0.5">
                  {tab.badge > 9 ? '9+' : tab.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}
